import { ChangeEvent, CSSProperties, useState, useEffect, useRef } from "react";
import React from 'react';
import Colors from "modules/Colors";
import Loader from 'elements/Loader';
import { clsx, styx } from "modules/Utils";
import ButtonSymphony from "./ButtonSymphony";
import debounce from 'lodash/debounce';
import Icon from "./Icon";
import Sty from "modules/Sty";

const BLUR_DELAY = 100;

export interface SelectOptionItem {
    value: string;
    title: string;
    description?: string;
    object?: any;
    thumbnailUrl?: string;
    icon?: string;
}

interface InputSymphonyProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur'> {
    className: string;
    style?: CSSProperties;
    styleInput?: CSSProperties;
    value: string;
    onChange?: (value: string) => void;
    onBlur?: (value: string) => void;
    isLoading?: boolean;
    /** Error message to display */
    error?: string;
    width?: number | string;
    maxWidth?: number | string;
    /** Maximum allowed length for the input */
    maxLength?: number;
    /** Helper text to display below the input */
    helperText?: string;
    /** Label to display above the input */
    label?: string;
    isSearch?: boolean;
    onSearch?: () => void;
    isRealtimeSearch?: boolean;
    /** Callback function when Enter key is pressed */
    onEnter?: () => void;
    /** Whether the input is a select input */
    isSelect?: boolean;
    /** Options for the select input */
    options?: SelectOptionItem[];
    /** Whether the select input is disabled */
    disabled?: boolean;
    /** Whether the select input is disabled style */
    isDisabledStyle?: boolean;
    /** Callback function to fetch selected options */
    fetchSelectedOptions?: () => void;
    /** Debounce time for fetching selected options */
    debounceTime?: number;
    /** Whether the select input is a dropdown */
    isSelectDropdown?: boolean;
    /** Optional callback when an option is selected from the dropdown */
    onOptionSelect?: (option: SelectOptionItem) => void;
    /** Whether the input should be a multiline textarea */
    isTextArea?: boolean;
    /** Number of rows for the textarea (only applies when isMultiline is true) */
    format?: 'text' | 'money' | 'number';
    rows?: number;
    /** Unique identifier for the input to prevent multiple dropdowns opening at once */
    inputId?: string;
    /** Pretext to display before the input */
    preText?: string;
}

const InputSymphony = ({
    className = '',
    style = {},
    styleInput = {},
    value,
    onChange,
    onBlur,
    isLoading = false,
    error,
    placeholder,
    width,
    maxWidth,
    maxLength,
    helperText,
    label = '',
    isSearch = false,
    onSearch = () => { },
    onEnter,
    isSelect = false,
    isSelectDropdown = false,
    options = [],
    disabled = false,
    isDisabledStyle = false,
    fetchSelectedOptions,
    debounceTime = 300,
    onOptionSelect: onOptionSelectProp,
    isRealtimeSearch = false,
    rows = 1,
    isTextArea = false,
    format = 'text',
    inputId = 'default',
    preText = '',
    type = 'text',
    ...props
}: InputSymphonyProps) => {
    /** Tracks whether the input is currently focused */
    const [isFocused, setIsFocused] = useState(false);
    /** Tracks loading state for debounced operations */
    const [isDebounceLoading, setIsDebounceLoading] = useState(false);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    /** Ref for the dropdown container to detect outside clicks */
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    /** Handle clicks outside the dropdown to close it */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if ((isSelectDropdown || isSelect) && (isDropdownOpen || isFocused)) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isSelectDropdown, isSelect, isDropdownOpen, isFocused]);

    /** Debounced function to fetch options when input has 3 or more characters */
    const debouncedFetchSelectedOptions = debounce(async (searchValue: string) => {
        if (fetchSelectedOptions && searchValue.length >= 3) {
            setIsDebounceLoading(true);
            await fetchSelectedOptions();
            setIsDebounceLoading(false);
        }
    }, debounceTime);

    /** Trigger debounced fetch when value changes */
    useEffect(() => {
        if (isSelect && value?.length >= 3) {
            setIsDebounceLoading(true);
        }

        if (isSelect) {
            debouncedFetchSelectedOptions(value);
        }

        return () => {
            debouncedFetchSelectedOptions.cancel();
            setIsDebounceLoading(false);
        };
    }, [value, isSelect]);

    /** Handles keydown events and triggers onEnter callback when Enter is pressed */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && onEnter) {
            onEnter();
        }
    };

    /** Handles focus events on the input */
    const handleFocus = () => {
        setIsFocused(true);

        if (isSelect) {
            setIsDropdownOpen(true);
        }
    };

    /** Handles blur events on the input */
    const handleBlur = () => {
        // Small delay to allow click events on options to fire
        setTimeout(() => {
            setIsFocused(false);
            if (onBlur) {
                onBlur(value);
            }
        }, BLUR_DELAY);
    };

    /** Handles both input changes and option selections */
    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (onChange) {
            let valueLocal = e.target.value;

            switch (format) {
                case 'number':
                case 'money':
                    valueLocal = valueLocal.replace(/[^0-9]/g, '');
                    break;
                default:
                    break;
            }
            onChange(valueLocal);
            if (isRealtimeSearch) {
                onSearch();
            }

            // Open dropdown for select inputs when typing
            if (isSelect && valueLocal.length > 0) {
                setIsDropdownOpen(true);
            }
        };
    }

    const handleOptionSelect = (option: SelectOptionItem) => {
        if (onOptionSelectProp) {
            onOptionSelectProp(option);
        }

        // Close the dropdown for both isSelect and isSelectDropdown
        setIsDropdownOpen(false);
        setIsFocused(false);

        // Blur the input to ensure dropdown doesn't reopen
        if (inputRef.current) {
            inputRef.current.blur();
        }
    }


    const renderSelectDropdownInput = () => {
        return (
            <>
                <div
                    className="inputSymphonySelectDropdown"
                    style={styx(
                        sty.inputSymphonyInput,
                        sty.inputSymphonySelectDropdown,
                        error ? sty.inputError : null,
                        isLoading ? sty.inputWithLoader : null,
                        isDropdownOpen ? sty.inputWithDropdownOpen : null,
                        styleInput
                    )}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                    {!value ?
                        <div
                            className="inputSymphonySelectDropdownPlaceholder"
                            style={{ ...Sty.text18grey, color: Colors.grey }}
                        >
                            {placeholder}
                        </div>
                        :
                        options.find(option => option.value === value)?.title || value
                    }
                </div>
                <Icon
                    className="inputSymphonySelectDropdownIcon"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    icon="chevronUp"
                    size={24}
                    color={Colors.grey}
                    style={styx(
                        sty.chevronContainer,
                        isDropdownOpen ? { transform: 'rotate(180deg)' } : {}
                    )}
                />
            </>
        )
    }

    const renderDefaultInput = () => {
        const input = (
            <input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                className="inputSymphonyDefaultInput"
                value={format === 'money' ? `$${value}` : value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={placeholder}
                maxLength={maxLength}
                type={type}
                disabled={disabled}
                style={styx(
                    preText ? sty.inputWithPreText : sty.inputSymphonyInput,
                    error ? sty.inputError : null,
                    (isLoading || isDebounceLoading) ? sty.inputWithLoader : null,
                    isSearch ?
                        styx(
                            sty.inputSymphonyInput_withSearch,
                            sty.inputSymphonyInput_withSearchIcon
                        )
                        :
                        null,
                    (isFocused || isDropdownOpen) && isSelect && options.length > 0 ? sty.inputWithDropdownOpen : null,
                    type === 'color' && sty.inputSymphonyInput_withColor,
                    isDisabledStyle ? sty.inputDisabledStyle : null,
                    styleInput,
                )}
                {...props}
            />
        )

        if (preText) {
            return (
                <div className="inputSymphonyPreText" style={sty.inputSymphonyPreText}>
                    <div
                        style={{
                            ...Sty.text18grey,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {preText}
                    </div>
                    {input}
                </div>
            )
        }

        return input;
    }

    const renderMultilineInput = () => {
        return (
            <textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                className="inputSymphonyTextarea"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={placeholder}
                maxLength={maxLength}
                rows={rows}
                style={styx(
                    sty.inputSymphonyInput,
                    sty.textareaInput,
                    error ? sty.inputError : null,
                    (isLoading || isDebounceLoading) ? sty.inputWithLoader : null,
                    styleInput,
                )}
            />
        )
    }

    const renderInput = () => {
        if (isSelectDropdown) {
            return renderSelectDropdownInput()
        } else if (isTextArea) {
            return renderMultilineInput()
        }

        return renderDefaultInput()
    }

    const renderDropdownOptions = () => {
        const shouldShowDropdown = (isSelect || isSelectDropdown) &&
            !disabled &&
            (isFocused || isDropdownOpen) &&
            options.length > 0;

        if (shouldShowDropdown) {
            return (
                <div
                    className="inputSymphonySelect"
                    style={sty.inputSymphonySelect}
                    data-input-id={inputId}
                >
                    {options
                        .filter(option =>
                            isSelectDropdown ||
                            !value ||
                            option.title.toLowerCase().includes(value.toLowerCase()) ||
                            option.value.toLowerCase().includes(value.toLowerCase())
                        )
                        .map((option) => (
                            <div
                                key={option.value}
                                className="inputSymphonySelectOption"
                                style={sty.inputSymphonySelectOption}
                                onClick={() => handleOptionSelect(option)}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: 8
                                    }}
                                >
                                    {option.thumbnailUrl &&
                                        <Icon
                                            className="inputSymphonySelectOptionThumbnail"
                                            icon={option.thumbnailUrl}
                                            iconType="custom"
                                            size={40}
                                        />
                                    }
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4
                                    }}>
                                        <div>
                                            {option.title}
                                        </div>
                                        {option.description &&
                                            <div style={sty.optionDescription}>{option.description}</div>
                                        }
                                    </div>
                                    {option.icon &&
                                        <Icon
                                            className="inputSymphonySelectOptionIcon"
                                            icon={option.icon}
                                            size={20}
                                        />
                                    }
                                </div>
                            </div>
                        ))
                    }
                </div>
            )
        }

        return null
    }

    return (
        <>
            <style>
                {`
                    .inputSymphonyElement input {
                        outline: none;
                        box-sizing: border-box;
                    }

                    .inputSymphonyElement input::placeholder {
                        color: ${Colors.grey};
                    }

                    .inputSymphonyElement input:focus {
                        outline: none;
                        outline-offset: -2px;
                    }

                    .inputSymphonyElement input:hover {
                        border-color: ${Colors.purple};
                    }

                    .inputSymphonySelectOption:hover {
                        background-color: ${Colors.purpleLightest};
                    }
                `}
            </style>
            <div
                ref={dropdownRef}
                className={clsx('inputSymphonyElement', className)}
                style={styx(
                    sty.wrapper,
                    width ? { width } : {},
                    maxWidth ? { maxWidth } : {},
                    type === 'color' && sty.wrapper_withColor,
                    style
                )}
                data-input-id={inputId}
            >
                <div className="inputContainer" style={sty.inputContainer}>
                    {label &&
                        <div className="inputSymphonyLabel" style={sty.inputSymphonyLabel}>
                            {label}
                        </div>
                    }
                    {isSearch &&
                        <Icon
                            className="inputSymphonySearchIcon"
                            style={styx(
                                sty.searchIconContainer,
                                label ? { top: 51 } : null
                            )}
                            icon="search"
                            color={Colors.grey}
                        />
                    }
                    <div
                        className="inputWithPrefixContainer"
                        style={sty.inputWithPrefixContainer}
                    >
                        {renderInput()}
                    </div>
                    {isSearch &&
                        <ButtonSymphony
                            className="inputSymphonySearchButton"
                            style={styx(
                                sty.inputSymphonySearchButton,
                                label ? sty.inputSymphonySearchButton_withLabel : null
                            )}
                            onClick={onSearch}
                            variant="text"
                            isLoading={isLoading}
                        >
                            Search
                        </ButtonSymphony>
                    }
                    {(isLoading || isDebounceLoading) &&
                        <div
                            className="inputSymphonyLoaderContainer"
                            style={sty.loaderContainer}
                        >
                            <Loader
                                height={24}
                                width={24}
                            />
                        </div>
                    }
                    {renderDropdownOptions()}
                </div>
                {helperText &&
                    <div className="inputSymphonyHelperText" style={sty.helperText}>
                        {helperText}
                    </div>
                }
                {error &&
                    <div className="inputSymphonyErrorText" style={sty.errorText}>
                        {error}
                    </div>
                }
            </div >
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        width: '100%'
    },
    inputContainer: {
        position: 'relative',
        width: '100%'
    },
    inputSymphonyInput: {
        width: '100%',
        paddingTop: 11,
        paddingBottom: 10,
        paddingLeft: 12,
        paddingRight: 12,
        fontSize: 18,
        color: Colors.textColor,
        backgroundColor: 'white',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        borderColor: Colors.greyLight,
        borderWidth: 1,
        borderStyle: 'solid',
    },
    inputWithPreText: {
        width: '100%',
        fontSize: 18,
        color: Colors.textColor,
    },
    inputSymphonyPreText: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        paddingTop: 11,
        paddingBottom: 10,
        paddingLeft: 12,
        paddingRight: 12,
        fontSize: 18,
        color: Colors.textColor,
        backgroundColor: 'white',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        borderColor: Colors.greyLight,
        borderWidth: 1,
        borderStyle: 'solid',
    },
    textareaInput: {
        resize: 'none'
    },

    inputError: {
        borderColor: Colors.red
    },
    inputWithLoader: {
        paddingRight: 48
    },
    loaderContainer: {
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    chevronContainer: {
        position: 'absolute',
        right: 12,
        top: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    errorText: {
        color: Colors.red,
        fontSize: 16,
    },
    inputSymphonyLabel: {
        fontSize: 14,
        marginBottom: 4,
        marginLeft: 4,
        color: Colors.greyDark
    },
    inputSymphonySearchButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        right: 12,
        top: 11,
    },
    inputSymphonySearchButton_withLabel: {
        top: 36,
    },
    inputSymphonyInput_withSearch: {
        paddingRight: 72,
    },
    searchIconContainer: {
        position: 'absolute',
        left: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 1
    },
    inputSymphonyInput_withSearchIcon: {
        paddingLeft: 40
    },
    inputWithPrefixContainer: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        position: 'relative'
    },
    inputWithPrefix: {
        paddingLeft: 'calc(12px + var(--prefix-width))'
    },
    inputSymphonySelect: {
        position: 'absolute',
        width: '100%',
        backgroundColor: 'white',
        overflowY: 'scroll',
        maxHeight: 200,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        borderLeft: `1px solid ${Colors.greyLight}`,
        borderRight: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        borderTop: 'none',
        zIndex: 1000,
        boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.1)',
    },
    inputSymphonySelectOption: {
        padding: 8,
        cursor: 'pointer',
        fontSize: 16,
    },
    inputSymphonySelectDropdown: {
        cursor: 'pointer',
        border: `1px solid ${Colors.greyLight}`,
        minHeight: 50,
        paddingRight: 45,
    },
    inputWithDropdownOpen: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
    },
    optionDescription: {
        ...Sty.text14grey,
        marginTop: 2
    },
    helperText: {
        ...Sty.text16grey
    },
    wrapper_withColor: {
        width: 'auto',
    },
    inputSymphonyInput_withColor: {
        padding: 0,
        width: 50,
        height: 50,
        borderWidth: 0,
        cursor: 'pointer'
    },
    inputDisabledStyle: {
        backgroundColor: Colors.greyLight,
        borderColor: Colors.greyLight,
        // color: Colors.grey,
    }
};

export default InputSymphony;